import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { UrgencyIndicator } from './UrgencyIndicator'

function getDaysUntil(dateString: string): number {
  return Math.ceil((new Date(dateString).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
}

interface TopQuotesHeaderProps {
  /** Carousel controls — they live on the title row at every width. */
  onScrollPrev: () => void
  onScrollNext: () => void
  /**
   * ISO date the customer's current policy expires. Omitted when we don't know it — the
   * countdown is hidden rather than shown against a guessed date.
   */
  policyExpirationDate?: string
  /** Greys out an arrow once the carousel has nothing further that way. */
  canScrollPrev?: boolean
  canScrollNext?: boolean
  /** A banner beside the heading when the header has room, on its own row below it otherwise; hidden on phones. */
  notice?: ReactNode
}

/** "Top Quotes" heading, carousel controls and the current-policy expiration countdown. */
export function TopQuotesHeader({
  onScrollPrev,
  onScrollNext,
  policyExpirationDate,
  canScrollPrev = true,
  canScrollNext = true,
  notice,
}: TopQuotesHeaderProps) {
  const daysUntilExpiration = policyExpirationDate ? getDaysUntil(policyExpirationDate) : null

  return (
    <header className="@container/top-quotes mb-8 flex flex-wrap items-center justify-between gap-3 max-md:mb-5">
      {/* "Top Quotes" doesn't fit beside the countdown and the arrows on a phone, and
          stacking them cost a whole row — so the copy shortens instead. */}
      <h1 className="text-ink text-3xl font-extrabold tracking-tight">
        <span className="max-md:hidden">Top Quotes</span>
        <span className="md:hidden">Quotes</span>
      </h1>
      {/* Measured against the header, not the window: a docked chat narrows the page. Where the heading,
          countdown and arrows leave no room it takes the next row. A block, so the banner (a size container,
          with no width of its own) fills it rather than collapsing. */}
      {notice && (
        <div className="order-last basis-full max-md:hidden @6xl/top-quotes:order-none @6xl/top-quotes:mr-auto @6xl/top-quotes:ml-3 @6xl/top-quotes:max-w-2xl @6xl/top-quotes:flex-1 @6xl/top-quotes:basis-auto">
          {notice}
        </div>
      )}
      <div className="ml-auto flex items-center gap-3">
        {daysUntilExpiration !== null && (
          <span className="text-ink-400 flex items-center gap-2 text-sm font-medium">
            <UrgencyIndicator daysRemaining={daysUntilExpiration} />
            {daysUntilExpiration} days until policy expiration
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            aria-label="Scroll left"
            onClick={onScrollPrev}
            disabled={!canScrollPrev}
            className="rounded-full"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            aria-label="Scroll right"
            onClick={onScrollNext}
            disabled={!canScrollNext}
            className="rounded-full"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </header>
  )
}
